<template>
  <v-container>

    <error-dialog :status="editDesignStatus" />
    <error-dialog :status="loadDesignStatus" />
    <error-dialog :status="submitStatus" />

    <sign-in-dialog v-model="showSignIn">
      <template v-slot:subtitle>Sign in to verify your email address</template>
    </sign-in-dialog>

    <dialog-scroll-lock
      v-if="designId"
      fullscreen persistent no-click-animation content-class="white fill-height"
      :transition="false"
      v-model="showDesign"
    >
      <design-edit :design-id="designId" :is-applying="true" @update="showDesign = false" />
    </dialog-scroll-lock>

    <h1 class="text-h3 text-md-h2 my-6 header-font">Ellacard design program</h1>
    <p class="text-md-h6">We are looking for creative artists and animators to help us build our catalog of <router-link :to="{ name: 'catalog-category', params: { category: 'greeting-cards' } }">greeting cards</router-link> and <router-link :to="{ name: 'catalog-category', params: { category: 'invitations' } }">invitations</router-link>. Ellacard offers a wide range of card types and aesthetics, including still, photo template, animated cards, and invitations. As such we are interested in many different artistic styles. We work with both freelance artists and studios.</p>

    <div class="mx-n3 d-flex justify-center relative overflow-hidden" style="min-height: 288px;">
      <v-img v-bind:class="{ absolute: $vuetify.breakpoint.smAndDown }" min-width="960" :aspect-ratio="3307 / 990" src="https://ellacard.com/start/header-cards-large.jpg" />
    </div>

    <h2 class="text-h4 mb-12">Opportunities</h2>
    <ul>
      <li class="mb-12">
        <h3 class="text-h5">Licensing / per-design rate</h3>
        <p>Ellacard will license your designs non-exclusively – you retain full ownership and can feature or sell your designs on other platforms</p>
      </li>
      <li class="mb-12">
        <h3 class="text-h5">Hourly / contracting</h3>
        <p>Ellacard contracts with freelance artists, firms, and collectives and offers negotiable hourly compensation</p>
      </li>
    </ul>

    <h2 class="text-h4 mt-16 mb-6">Artist rights</h2>
    <p class="mb-12">Artists retain <i>full ownership</i> of licensed designs. This means that you can continue to feature or sell your design on other platforms and remove it from Ellacard at any time. Any designs / samples submitted during the application process will <i>not</i> be surfaced until an agreement is reached.</p>

    <h2 class="text-h4 mt-16 mb-6">How to apply</h2>
    <p class="mb-6">To apply, please fill out the form below. Part of this process includes submitting a sample of your work and using the Ellacard design tools to annotate it with additional information. We prioritize applicants who take the time to create <i>interactive</i> designs, i.e. with user-editable text or photo slots.</p>
    <p class="mb-9">Please contact us with any questions: <a href="mailto:design@ellacard.com">design@ellacard.com</a></p>

    <v-form ref="form" @submit.prevent="submit">
    <v-card max-width="800" outlined>
      <v-card-title>Ellacard design program application form</v-card-title>
      <v-card-text>
        <v-text-field label="Full name" :disabled="didSubmit" :rules="[ v => !!name ]" v-model="name" />

        <v-text-field
          label="Email address" readonly
          :value="isSignedIn ? global.profile.email : null"
          :rules="[ v => isSignedIn ]"
          :disabled="didSubmit"
          @click="showSignIn = true"
        >
          <template v-slot:append-outer>
            <v-btn small :disabled="didSubmit" @click="showSignIn = true" v-text="isSignedIn ? 'Change…' : 'Sign in…'" />
          </template>
        </v-text-field>

        <v-autocomplete
          label="Country of residence" type="country" item-value="code" item-text="name"
          :disabled="didSubmit"
          :items="COUNTRIES"
          :rules="[ v => !!country ]"
          v-model="country"
        />

        <div>What type of opportunity are you interested in?</div>
        <div class="d-flex flex-wrap" style="max-width: 500px;">
          <v-checkbox
            class="mb-sm-3"
            hide-details label="Licensing / per-design rate" value="per_design"
            :disabled="didSubmit"
            :rules="[ v => !!opportunities.length ]"
            v-model="opportunities"
          />
          <v-spacer />
          <v-text-field
            v-if="opportunities.includes('per_design')"
            class="ml-8"
            style="max-width: 200px;"
            hide-details label="Expected per-design rate (USD)" placeholder="0.00" persistent-placeholder prefix="$"
            :disabled="didSubmit"
            :rules="[ v => /^\d+(\.\d\d)?$/.test(v || '') ]"
            v-model="perDesignRate"
          />
        </div>

        <div class="d-flex flex-wrap" style="max-width: 500px;">
          <v-checkbox
            class="mb-sm-3"
            hide-details label="Hourly / contracting" value="per_hour"
            :disabled="didSubmit"
            :rules="[ v => !!opportunities.length ]"
            v-model="opportunities"
          />
          <v-spacer />
          <v-text-field
            v-if="opportunities.includes('per_hour')"
            class="ml-8"
            style="max-width: 200px;"
            hide-details label="Expected hourly rate (USD)" placeholder="0.00" persistent-placeholder prefix="$"
            :disabled="didSubmit"
            :rules="[ v => /^\d+(\.\d\d)?$/.test(v || '') ]"
            v-model="perHourRate"
          />
        </div>

        <div class="mt-6 float-right mb-1 ml-2 relative" style="width: 120px;">
          <div class="absolute-fill d-flex justify-center align-center" style="z-index: 2;">
            <v-btn
              v-blur
              fab
              :loading="editDesignStatus.pending || loadDesignStatus.pending"
              :disabled="didSubmit"
              @click="editDesign"
            >
              <v-icon v-html="mdiPencil" />
            </v-btn>
          </div>
          <v-textarea
            class="preview"
            style="z-index: 1;"
            hide-details outlined auto-grow readonly
            :disabled="didSubmit"
            :rules="[ v => !!designId ]"
            :value="designId"
          />
          <div class="absolute-fill overflow-hidden" style="border-radius: 4px; aspect-ratio: 5 / 7;">
            <card-preview v-if="design" :card="design" />
          </div>
        </div>

        <div class="mt-6">Upload a sample of your work as a card / invitation using the Ellacard design tools</div>
        <ul>
          <li>Make sure your design has a meaningful title</li>
          <li>Create text directly in the Ellacard editor with one of the built-in fonts (instead of including it as part of an image)</li>
          <li>Add tags to your design so that users can search for it (use the <v-icon small v-html="mdiTag" /> menu)</li>
          <li>Set a default theme for your design (use the <v-icon small v-html="mdiPalette" /> menu)</li>
          <li>If you're submitting an invitation, link any relevant text to the corresponding <i>event</i> (right-click on a text block and select "Link text to event")</li>
        </ul>
        <div style="clear: both;" />

        <div class="mt-8 mb-1">Please share any additional information, e.g. a link to a profile showing relevant work</div>
        <v-textarea outlined auto-grow rows="3" :disabled="didSubmit" v-model="supplemental" />

        <div class="mb-1">How did you find out about the Ellacard design program?</div>
        <v-textarea outlined auto-grow rows="3" :disabled="didSubmit" v-model="referral" />

      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn :loading="submitStatus.pending" :disabled="didSubmit" type="submit" color="primary">Submit</v-btn>
      </v-card-actions>
    </v-card>
    </v-form>

    <div class="mx-n3 d-flex justify-center relative overflow-hidden" style="min-height: 288px;">
      <v-img v-bind:class="{ absolute: $vuetify.breakpoint.smAndDown }" min-width="960" :aspect-ratio="3307 / 990" src="https://ellacard.com/start/header-cards-large.jpg" />
    </div>

  </v-container>
</template>


<style>
.preview {
  max-width: 120px;
}
.preview .v-input__slot {
  aspect-ratio: 5 / 7;
  padding: 12px;
  cursor: pointer !important;
}
.preview textarea {
  color: transparent !important;
}
</style>


<script>
import CardPreview from '@/components/CardPreview.vue';
import DesignEdit from '@/views/DesignEdit.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import COUNTRIES from '@/utils/countries.js';
import { COUNTRY_CODE } from '@/utils/intl.js';
import { mdiTag, mdiPalette, mdiPencil } from '@mdi/js';

export default {
  name: 'design',

  routerViewMeta: {
    title: 'Design'
  },

  components: {
    CardPreview,
    DesignEdit,
    SignInDialog
  },

  data() {
    return {
      showSignIn: false,
      showDesign: false,
      didSubmit: false,
      editDesignStatus: this.$track('editDesign'),
      loadDesignStatus: this.$track('loadDesign'),
      submitStatus: this.$track('submit'),
      name: '',
      country: COUNTRY_CODE || 'US',
      opportunities: [],
      designId: null,
      supplemental: '',
      referral: '',
      design: null,
      perHourRate: null,
      perDesignRate: null
    };
  },

  created() {
    Object.assign(this, { COUNTRIES: COUNTRIES.map(([ name, code ]) => ({ name, code })), mdiTag, mdiPalette, mdiPencil });
  },

  methods: {
    async editDesign() {
      if (!this.global.profile)
        throw new VisibleError('Please enter your email address first');
      if (!this.designId)
        this.designId = await this.$call('create-design');
      this.showDesign = true;
    },

    async loadDesign() {
      this.design = await this.$call('get-design', { design_id: this.designId });
    },

    async submit() {
      if (this.didSubmit)
        return;
      if (!this.$refs.form.validate())
        throw new VisibleError('Missing required fields');
      const r = await this.$confirm({
        title: 'Are you sure?',
        msg: `Please make sure your sample card / invitation is as complete as possible. Being able to work within our design tools is important. Note that you won't be able to make changes or apply again for 30 days once your application has been submitted. Are you sure you want to continue?`
      });
      if (r == 'No')
        return;
      const toPrice = (k, v) => (v && this.opportunities.includes(k)) ? (v.includes('.') ? v : (v + '.00')) : undefined;
      const params = {
        name: this.name,
        country: this.country,
        opportunities: this.opportunities,
        per_hour_rate: toPrice('per_hour', this.perHourRate),
        per_design_rate: toPrice('per_design', this.perDesignRate),
        design_id: this.designId,
        supplemental: this.supplemental,
        referral: this.referral
      };
      const { err } = await this.$call('submit-designer-application', params);
      this.didSubmit = true;
      if (err)
        throw new VisibleError(err);
      this.$confirm({
        title: 'Application received',
        msg: 'Your application has been received and is being reviewed. Expect a response within 5-10 business days.',
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });
    }
  },

  watch: {
    showDesign(v) {
      if (!v)
        this.loadDesign();
    }
  }
}
</script>
